import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment,
  MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import MenuImagePicker, { emptyImage } from './MenuImagePicker';
import { TAGS } from './menuUtils';

const emptyDish = { name: '', description: '', price: '', price_options: [], tags: [], featured: false, available: true, active: true };

/**
 * Create / edit a dish. `onSave(payload, image)` does the API calls; the
 * photo is uploaded after the dish exists.
 */
const DishDialog = ({ open, dish, sections, sectionUuid, currency, onClose, onSave, saving, t }) => {
  const [form, setForm] = useState(emptyDish);
  const [mode, setMode] = useState('single');
  const [section, setSection] = useState('');
  const [image, setImage] = useState(emptyImage());

  useEffect(() => {
    if (!open) return;
    if (dish) {
      setForm({
        name: dish.name, description: dish.description || '', price: dish.price ?? '',
        price_options: (dish.price_options || []).map((o) => ({ label: o.label, price: String(o.price) })),
        tags: dish.tags || [], featured: !!dish.featured, available: !!dish.available, active: !!dish.active,
      });
      setMode(dish.price_options?.length ? 'options' : 'single');
      setSection(sections.find((s) => s.id === dish.digital_menu_section_id)?.uuid || sectionUuid);
      setImage(emptyImage(dish.image_url));
    } else {
      setForm(emptyDish);
      setMode('single');
      setSection(sectionUuid);
      setImage(emptyImage());
    }
  }, [open, dish, sectionUuid, sections]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setOption = (i, patch) => set({ price_options: form.price_options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const toggleTag = (tag) => set({ tags: form.tags.includes(tag) ? form.tags.filter((x) => x !== tag) : [...form.tags, tag] });

  const switchMode = (next) => {
    if (!next) return;
    setMode(next);
    if (next === 'options' && form.price_options.length === 0) {
      set({ price_options: [{ label: t('menus.sizeSmall'), price: form.price === '' ? '' : String(form.price) }, { label: t('menus.sizeLarge'), price: '' }] });
    }
  };

  const options = form.price_options.filter((o) => o.label.trim() && o.price !== '');
  const valid = form.name.trim() && section && (mode === 'single' || options.length > 0);

  const submit = () => {
    if (!valid) return;
    onSave({
      section,
      name: form.name.trim(),
      description: form.description.trim() || null,
      price: mode === 'single' && form.price !== '' ? Number(form.price) : null,
      price_options: mode === 'options' ? options.map((o) => ({ label: o.label.trim(), price: Number(o.price) })) : null,
      tags: form.tags,
      featured: form.featured,
      available: form.available,
      active: form.active,
    }, image);
  };

  const money = { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dish ? t('menus.editDish') : t('menus.newDish')}</DialogTitle>
      <DialogContent>
        {/* DialogContent drops its top padding under a title: leave room for the outlined label. */}
        <Grid container spacing={3} sx={{ pt: 1.5 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              <TextField autoFocus required fullWidth label={t('menus.dishName')} value={form.name} inputProps={{ maxLength: 120 }}
                onChange={(e) => set({ name: e.target.value })} />
              <TextField fullWidth multiline minRows={2} label={t('menus.dishDescription')} value={form.description} inputProps={{ maxLength: 1000 }}
                helperText={t('menus.dishDescriptionHint')} onChange={(e) => set({ description: e.target.value })} />
              <TextField select fullWidth label={t('menus.section')} value={section} onChange={(e) => setSection(e.target.value)}>
                {sections.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.name}</MenuItem>)}
              </TextField>

              <Box>
                <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => switchMode(v)} sx={{ mb: 1.5 }}>
                  <ToggleButton value="single">{t('menus.priceSingle')}</ToggleButton>
                  <ToggleButton value="options">{t('menus.priceOptions')}</ToggleButton>
                </ToggleButtonGroup>
                {mode === 'single' ? (
                  <TextField fullWidth type="number" label={t('menus.price')} value={form.price} InputProps={money}
                    inputProps={{ min: 0, step: '0.01' }} helperText={t('menus.priceHint')}
                    onChange={(e) => set({ price: e.target.value })} />
                ) : (
                  <Stack spacing={1}>
                    {form.price_options.map((o, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <TextField size="small" label={t('menus.optionLabel')} value={o.label} inputProps={{ maxLength: 40 }} sx={{ flex: 1.4 }}
                          onChange={(e) => setOption(i, { label: e.target.value })} />
                        <TextField size="small" type="number" label={t('menus.price')} value={o.price} InputProps={money} inputProps={{ min: 0, step: '0.01' }} sx={{ flex: 1 }}
                          onChange={(e) => setOption(i, { price: e.target.value })} />
                        <IconButton size="small" aria-label={t('menus.removeOption')} onClick={() => set({ price_options: form.price_options.filter((_, j) => j !== i) })}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    {form.price_options.length < 8 && (
                      <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                        onClick={() => set({ price_options: [...form.price_options, { label: '', price: '' }] })}>
                        {t('menus.addOption')}
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('menus.tags')}</Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  {TAGS.map((tag) => (
                    <Chip key={tag} label={t(`menus.tag.${tag}`)} clickable onClick={() => toggleTag(tag)}
                      color={form.tags.includes(tag) ? 'primary' : 'default'} variant={form.tags.includes(tag) ? 'filled' : 'outlined'} />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              <MenuImagePicker value={image} onChange={setImage} disabled={saving} t={t} height={220} hint={t('menus.dishPhotoHint')} />
              <Box>
                <FormControlLabel control={<Switch checked={form.available} onChange={(e) => set({ available: e.target.checked })} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>{t('menus.available')}</Typography><Typography variant="caption" color="text.secondary">{t('menus.availableHint')}</Typography></Box>} />
              </Box>
              <Box>
                <FormControlLabel control={<Switch checked={form.featured} onChange={(e) => set({ featured: e.target.checked })} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>{t('menus.featured')}</Typography><Typography variant="caption" color="text.secondary">{t('menus.featuredHint')}</Typography></Box>} />
              </Box>
              <Box>
                <FormControlLabel control={<Switch checked={form.active} onChange={(e) => set({ active: e.target.checked })} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>{t('menus.visible')}</Typography><Typography variant="caption" color="text.secondary">{t('menus.visibleHint')}</Typography></Box>} />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !valid}>{t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DishDialog;
